import { useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/app/dashboard/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import {
  getBillingInformation,
  getPaymentMethodUrl,
  getBillingPlans,
  getOrganisationBillingPlan,
  upgradeBillingPlan,
  getOrganisationMembers,
} from '@/client/sdk.gen';
import { useToast } from '@/hooks/use-toast';
import { FaCcVisa, FaCcMastercard } from 'react-icons/fa';
import {
  BillingPlanResponse,
  CardDetails,
  OrganisationMemberResponse,
} from '@/client/types.gen';
import { AlertCircle, Check, CreditCard, Sparkles, Users } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  listCountries,
  getOrganisationDetails,
  updateOrganisationAddress,
  updateOrganisationDetails,
} from '@/client/sdk.gen';
import { OrganisationResponse } from '@/client/types.gen';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

export default function BillingPage() {
  const { toast } = useToast();
  const [paymentMethods, setPaymentMethods] = useState<CardDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMethods, setIsLoadingMethods] = useState(true);
  const [address, setAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
  });
  const [phone, setPhone] = useState('');
  const [countries, setCountries] = useState<
    Array<{ code: string; name: string }>
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [orgDetails, setOrgDetails] = useState<OrganisationResponse | null>(
    null
  );
  const [billingPlans, setBillingPlans] = useState<BillingPlanResponse[]>([]);
  const [currentPlan, setCurrentPlan] = useState<BillingPlanResponse | null>(
    null
  );
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanResponse | null>(
    null
  );
  const [members, setMembers] = useState<OrganisationMemberResponse[]>([]);

  const fetchBillingInformation = async () => {
    try {
      setIsLoadingMethods(true);
      const response = await getBillingInformation();
      setPaymentMethods(response.data?.payment_methods ?? []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load payment methods',
      });
    } finally {
      setIsLoadingMethods(false);
    }
  };

  const fetchOrgDetails = async () => {
    try {
      const response = await getOrganisationDetails();
      if (response.data) {
        setOrgDetails(response.data);
        if (response.data.address) {
          setAddress({
            line1: response.data.address.address_line_1 || '',
            line2: response.data.address.address_line_2 || '',
            city: response.data.address.city || '',
            state: response.data.address.state || '',
            postal_code: response.data.address.postal_code || '',
            country: response.data.address.country_code || '',
          });
        }
        setPhone(response.data.phone || '');
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load organization details',
      });
    }
  };

  const fetchBillingPlans = async () => {
    try {
      setIsLoadingPlans(true);
      const plansResponse = await getBillingPlans();
      const currentPlanResponse = await getOrganisationBillingPlan();

      if (plansResponse.data) {
        setBillingPlans(plansResponse.data);
      }

      if (currentPlanResponse.data?.billing_plan) {
        setCurrentPlan(currentPlanResponse.data.billing_plan);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load billing plans',
      });
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await getOrganisationMembers();
      if (response.data) {
        setMembers(response.data.members);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load team members',
      });
    }
  };

  useEffect(() => {
    fetchBillingInformation();
    fetchCountries();
    fetchOrgDetails();
    fetchBillingPlans();
    fetchTeamMembers();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await listCountries();
      setCountries(response.data?.countries ?? []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load countries',
      });
    }
  };

  const handleAddPaymentMethod = async () => {
    setIsLoading(true);
    try {
      const response = await getPaymentMethodUrl();
      window.location.href = response.data?.url ?? '';
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to initiate payment method addition',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAddress = async () => {
    setIsSaving(true);
    try {
      await updateOrganisationAddress({
        body: {
          address_line_1: address.line1,
          address_line_2: address.line2,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
          country_code: address.country,
          country_name:
            countries.find((c) => c.code === address.country)?.name || '',
        },
      });

      await updateOrganisationDetails({
        body: {
          phone,
          name: orgDetails?.name || '',
          description: orgDetails?.description || '',
        },
      });

      toast({
        title: 'Success',
        description: 'Organization billing information updated successfully',
      });

      await fetchOrgDetails();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update organization billing information',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpgradePlan = (plan: BillingPlanResponse) => {
    if (paymentMethods.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Payment Method Required',
        description: 'Please add a payment method before upgrading your plan',
      });
      return;
    }
    setSelectedPlan(plan);
    setShowUpgradeDialog(true);
  };

  const handleConfirmUpgrade = async () => {
    if (!selectedPlan) return;

    setIsUpgrading(true);
    try {
      await upgradeBillingPlan({
        body: {
          plan_id: selectedPlan.id,
        },
      });

      toast({
        title: 'Success',
        description: `Successfully upgraded to ${selectedPlan.name} plan`,
      });

      // Refetch to get updated plan
      await fetchBillingPlans();
      setShowUpgradeDialog(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to upgrade plan. Please try again.',
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const getEstimatedMonthlyCost = () => {
    if (!selectedPlan || !members) return 0;
    return (
      (selectedPlan.price_per_month_per_user_in_cents * members.length) / 100
    );
  };

  const hasPaymentMethod = paymentMethods.length > 0;

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Home', href: '/dashboard' },
        { label: 'Team Management', href: '#' },
        { label: 'Billing', href: '/dashboard/team-management/billing' },
      ]}
    >
      <div className="p-6">
        {!hasPaymentMethod && (
          <Alert variant="warning" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Payment Method Required</AlertTitle>
            <AlertDescription>
              Please add a payment method before upgrading your plan. You won't
              be able to upgrade until a valid payment method is added.
            </AlertDescription>
          </Alert>
        )}

        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2">Billing</h1>
          <p className="text-muted-foreground">
            Manage your subscription, payment methods, and billing information
          </p>
        </div>

        {/* Plans Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Subscription Plans</h2>

          {isLoadingPlans ? (
            <div className="flex justify-center py-8">
              <ClipLoader
                size={24}
                color="currentColor"
                aria-label="Loading plans"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {billingPlans.map((plan) => (
                <Card
                  key={plan.id}
                  className={`relative overflow-hidden ${
                    currentPlan?.id === plan.id ? 'border-2 border-primary' : ''
                  }`}
                >
                  {plan.price_per_month_per_user_in_cents === 0 && (
                    <div className="absolute top-0 left-0 bg-green-500 text-white py-1 px-3 rounded-br-md text-xs font-semibold">
                      Free
                    </div>
                  )}
                  {currentPlan?.id === plan.id && (
                    <div className="absolute top-0 right-0 bg-primary text-primary-foreground py-1 px-3 rounded-bl-md text-xs font-semibold">
                      Current Plan
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      {plan.code === 'premium' && (
                        <Sparkles className="h-5 w-5 text-yellow-500" />
                      )}
                    </div>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-3xl font-bold mb-1">
                        {formatPrice(plan.price_per_month_per_user_in_cents)}
                        <span className="text-sm font-normal text-muted-foreground">
                          /user/month
                        </span>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Check className="h-4 w-4 mr-2 text-green-500" />
                        <span>
                          {plan.price_per_month_per_user_in_cents === 0
                            ? '2 team members maximum'
                            : 'Unlimited team members'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-2 text-green-500" />
                        <span>Team collaboration tools</span>
                      </div>
                      {plan.code === 'premium' && (
                        <>
                          <div className="flex items-center">
                            <Check className="h-4 w-4 mr-2 text-green-500" />
                            <span>Priority support</span>
                          </div>
                          <div className="flex items-center">
                            <Check className="h-4 w-4 mr-2 text-green-500" />
                            <span>Advanced features</span>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className={`w-full ${
                        plan.price_per_month_per_user_in_cents > 0
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600'
                          : ''
                      }`}
                      disabled={
                        currentPlan?.id === plan.id ||
                        (plan.price_per_month_per_user_in_cents === 0 &&
                          currentPlan?.price_per_month_per_user_in_cents !==
                            undefined &&
                          currentPlan.price_per_month_per_user_in_cents > 0) ||
                        (!hasPaymentMethod &&
                          plan.price_per_month_per_user_in_cents > 0)
                      }
                      onClick={() => handleUpgradePlan(plan)}
                    >
                      {currentPlan?.id === plan.id
                        ? 'Current Plan'
                        : plan.price_per_month_per_user_in_cents === 0 &&
                          currentPlan?.price_per_month_per_user_in_cents !==
                            undefined &&
                          currentPlan.price_per_month_per_user_in_cents > 0
                        ? 'Downgrade Not Supported'
                        : !hasPaymentMethod &&
                          plan.price_per_month_per_user_in_cents > 0
                        ? 'Add Payment Method First'
                        : 'Upgrade'}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
          <div className="grid gap-6">
            {isLoadingMethods ? (
              <div className="flex justify-center py-8">
                <ClipLoader
                  size={24}
                  color="currentColor"
                  aria-label="Loading payment methods"
                />
              </div>
            ) : paymentMethods.length > 0 ? (
              paymentMethods.map((method) => (
                <Card key={method.last4}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {method.brand === 'visa' && (
                        <FaCcVisa className="h-6 w-6" />
                      )}
                      {method.brand === 'mastercard' && (
                        <FaCcMastercard className="h-6 w-6" />
                      )}
                      •••• {method.last4}
                    </CardTitle>
                    <CardDescription>
                      Expires {method.exp_month}/{method.exp_year}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-4">
                  <div className="text-center text-muted-foreground mb-4">
                    No payment methods found
                  </div>
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      You need to add a payment method before you can upgrade to
                      a paid plan. Click the button below to add a payment
                      method.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={handleAddPaymentMethod}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <ClipLoader
                  size={16}
                  color="currentColor"
                  className="mr-2"
                  aria-label="Loading"
                />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              {paymentMethods.length > 0
                ? 'Add Another Payment Method'
                : 'Add Payment Method'}
            </Button>
          </div>
        </div>

        {/* Upgrade Dialog */}
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upgrade to {selectedPlan?.name}</DialogTitle>
              <DialogDescription>
                You are about to upgrade to the {selectedPlan?.name} plan.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">New Plan</span>
                    <Badge variant="outline">{selectedPlan?.name}</Badge>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Price per user</span>
                    <span className="font-medium">
                      {selectedPlan &&
                        formatPrice(
                          selectedPlan.price_per_month_per_user_in_cents
                        )}
                      /month
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Current team size</span>
                    <span className="font-medium">
                      {members.length} members
                    </span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center font-semibold">
                      <span>Estimated monthly cost</span>
                      <span>${getEstimatedMonthlyCost().toFixed(2)}/month</span>
                    </div>
                  </div>
                </div>

                <Alert className="bg-amber-50 border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    Your billing will be prorated for the remainder of the
                    current billing cycle. You'll only be charged for each team
                    member from the date they were added to your organization.
                  </AlertDescription>
                </Alert>
              </div>
            </div>

            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowUpgradeDialog(false)}
                disabled={isUpgrading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUpgrade}
                disabled={isUpgrading}
                className={
                  !selectedPlan
                    ? ''
                    : (selectedPlan.price_per_month_per_user_in_cents ?? 0) > 0
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600'
                    : ''
                }
              >
                {isUpgrading ? (
                  <>
                    <ClipLoader
                      size={16}
                      color="currentColor"
                      className="mr-2"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Confirm Upgrade
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
